"use client";

import { useSidebarStore } from "@/zustand-hooks/sidebar";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Link } from "@/i18n/routing";
import SublinkedLink from "./SublinkedLink";
import SublinkedLinkMobile from "./SublinkedLinkMobile";
import { useUser } from "@/zustand-hooks/user-hook";
import { useFilteredLinks } from "@/utils/filterLinksByPermissions";
import { Tooltip } from "@mui/material";
import { useTranslations } from "next-intl";
import Logo from "./Logo";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTrigger } from "../ui/sheet";
import { MenuIcon } from "lucide-react";
import Responsive from "./Responsive";
import useIsMounted from "@/hooks/useIsMounted";


const Sidebar = () => {
    const translate_page_names = useTranslations("page_names");
    const { isCollapsed } = useSidebarStore();
    const [mobileOpen, setMobileOpen] = useState(false);
    const user = useUser((state) => state.user);
    const isMounted = useIsMounted();
    const filteredLinks = useFilteredLinks();
    const closeMobileMenu = () => setMobileOpen(false);

    if (!isMounted) return null

    return (
        <>
            <Responsive breakpoint={'md'}>
                <div
                    className={cn(
                        "flex flex-col flex-1 text-sm justify-start overflow-y-auto overflow-x-hidden h-screen py-8 bg-gradient-to-b from-chrome-900 via-chrome-800 to-chrome-900 dark:from-chrome-950 dark:via-chrome-900 dark:to-chrome-950 text-chrome-100 transition-all duration-300 border-r border-chrome-700/50 shadow-2xl",
                        !!isCollapsed
                            ? "min-w-[80px] max-w-[80px] items-center px-2"
                            : "min-w-[240px] max-w-[280px] w-auto px-6"
                    )}
                >
                    <div className="flex items-center justify-center mb-8">
                        <Logo size={isCollapsed ? 80 : 120} />
                    </div>

                    {!isCollapsed && (
                        <div className="h-px bg-gradient-to-r from-transparent via-chrome-600 to-transparent mb-8"></div>
                    )}

                    <nav
                        className={cn(
                            "flex max-h-full no-scrollbar overflow-y-auto flex-col space-y-2",
                            isCollapsed && "items-center"
                        )}
                    >
                        {filteredLinks?.map((link) => {
                            if (user?.id && link.url === "/login") {
                                return
                            }
                            // COND RENDERING IF SUBLINKS EXIST
                            // @ts-ignore
                            return !!link?.subLinks ? (
                                <SublinkedLink key={link.url} link={link} />
                            ) : (
                                <li className="group list-none" key={link.title}>
                                    <Tooltip
                                        title={translate_page_names(link.title)}
                                        placement="right"
                                    >
                                        <Link
                                            className={cn(
                                                "flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white/10 hover:scale-105 active:scale-95 relative overflow-hidden",
                                                isCollapsed ? "justify-center w-12 h-12" : "justify-start"
                                            )}
                                            href={link.url}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-brand-500/0 to-brand-600/0 group-hover:from-brand-500/10 group-hover:to-brand-600/10 transition-all duration-300 rounded-xl"></div>
                                            <span className="relative z-10 text-chrome-400 group-hover:text-white transition-colors duration-200">
                                                {link.icon}
                                            </span>
                                            {!isCollapsed && (
                                                <span className="relative z-10 font-medium text-chrome-400 group-hover:text-white transition-colors duration-200">
                                                    {translate_page_names(link.title)}
                                                </span>
                                            )}
                                        </Link>
                                    </Tooltip>
                                </li>
                            );
                        })}
                    </nav>
                </div>
            </Responsive>
            <Responsive query="(max-width: 900px)">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger className="block lg:hidden absolute cursor-pointer" asChild>
                        <div className="fixed top-4 left-4 z-50 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-slate-800 to-slate-700 text-white shadow-lg hover:from-slate-700 hover:to-slate-600 transition-all duration-200 hover:scale-105">
                            <MenuIcon size={20} />
                        </div>
                    </SheetTrigger>
                    <SheetDescription></SheetDescription>
                    <SheetContent className="bg-gradient-to-b from-chrome-900 via-chrome-800 to-chrome-900 dark:from-chrome-950 dark:via-chrome-900 dark:to-chrome-950 border-chrome-700" side={"left"}>
                        <SheetHeader>
                            <div className="flex items-center gap-3 mb-6">
                                <Logo size={50} />
                            </div>
                            <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent mb-6"></div>
                        </SheetHeader>
                        <nav className="flex max-h-[calc(100dvh-230px)] no-scrollbar overflow-y-auto flex-col space-y-2">
                            {filteredLinks?.map((link) => {
                                if (user?.id && link.url === "/login") return;
                                // COND RENDERING IF SUBLINKS EXIST
                                // @ts-ignore
                                return !!link?.subLinks ? (
                                    <SublinkedLinkMobile key={link.url} link={link} onNavigate={closeMobileMenu} />
                                ) : (
                                    <li className="group" key={link.title}>
                                        <Tooltip
                                            title={translate_page_names(link.title)}
                                            placement="right"
                                        >
                                            <Link
                                                className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white/10 hover:scale-105 active:scale-95 relative overflow-hidden text-chrome-100"
                                                href={link.url}
                                                onClick={closeMobileMenu}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-brand-500/0 to-brand-600/0 group-hover:from-brand-500/10 group-hover:to-brand-600/10 transition-all duration-300 rounded-xl"></div>
                                                <span className="relative z-10 text-chrome-400 group-hover:text-white transition-colors duration-200">
                                                    {link.icon}
                                                </span>
                                                <span className="relative z-10 font-medium text-chrome-400 group-hover:text-white transition-colors duration-200">
                                                    {translate_page_names(link.title)}
                                                </span>
                                            </Link>
                                        </Tooltip>
                                    </li>
                                );
                            })}
                        </nav>
                    </SheetContent>
                </Sheet>
            </Responsive>

        </>

    );
};

export default Sidebar;
